// Firebase initialization.
// NOTE: these values are *public* client config (they ship to the browser by design).
// Security is enforced by Firestore/Storage rules + Auth, not by hiding these keys.
import { initializeApp } from 'firebase/app'
import { initializeAppCheck, ReCaptchaEnterpriseProvider } from 'firebase/app-check'
import { getAuth, connectAuthEmulator } from 'firebase/auth'
import { getFirestore, initializeFirestore, connectFirestoreEmulator } from 'firebase/firestore'
// NOT HERE: Storage and Functions. App.jsx imports this module, so every import above is downloaded by
// every public page (the availability link, the surveys, the kiosk, the sign-in) before it appears.
// Storage lives in ./firebaseStorage, imported only by code that moves files. Functions has no
// client: nothing calls a function directly (CLAUDE.md Never 13, the request-document pattern).
// App Check stays: on production it must start before Firestore makes its first request.

// Public client config per deploy target. `vite build --mode staging` selects staging; the default
// build and `npm run dev` use production. These values are public by design — security is enforced
// by Auth + Firestore/Storage rules, not by hiding them.
// Tree-shakeable selection: Vite replaces `import.meta.env.MODE` with a string literal at build time, so
// for any non-staging build the `=== 'staging'` test folds to false and terser DEAD-CODE-ELIMINATES the
// entire staging branch — a production bundle then contains NO staging projectId at all. The
// check-deploy-mode guard relies on exactly that (it blocks a prod deploy if the staging ID appears in
// the bundle). Do NOT refactor this back into an object-keyed lookup (`CONFIGS[MODE]`) — that keeps both
// configs in the bundle and makes every prod deploy fail the guard.
const firebaseConfig = import.meta.env.MODE === 'staging'
  ? {
    apiKey: 'AIzaSyAjvv6YEFCEEiASzE5tmzKMIY65H0PxPxo',
    authDomain: 'fitproof-inciteos-stagin-837f0.firebaseapp.com',
    projectId: 'fitproof-inciteos-stagin-837f0',
    storageBucket: 'fitproof-inciteos-stagin-837f0.firebasestorage.app',
    messagingSenderId: '481845492620',
    appId: '1:481845492620:web:9735e5cfa911c3ad7d74a9',
    measurementId: 'G-FZQBV7DT7W',
  }
  : {
    apiKey: 'AIzaSyBhPIndLAVixum5F7Gt-t70NACT2YuRkOY',
    // Same-site authDomain (app is served from this host) so the signInWithRedirect
    // fallback survives third-party-storage blocking (Safari/Firefox). The Entra
    // redirect URI customers register is https://incite.fit-proof.com/__/auth/handler.
    authDomain: 'incite.fit-proof.com',
    projectId: 'fitproof-inciteos',
    storageBucket: 'fitproof-inciteos.firebasestorage.app',
    messagingSenderId: '706946034065',
    appId: '1:706946034065:web:d9d8cb197bb10ae2d41230',
  }

// Which Firebase project this build talks to. Used to badge the UI as a sandbox so a
// non-production environment is never mistaken for prod (see <DevBanner/>). Note: plain
// `npm run dev` falls back to the production config, so it is NOT a sandbox — the badge
// correctly stays off, which is your signal that local dev is pointed at live data.
export const ENV = firebaseConfig.projectId === 'fitproof-inciteos' ? 'production' : 'staging'
export const IS_DEV = ENV !== 'production'

// HIDE THE SANDBOX MARKER — FOR THE APP REVIEW BUILD ONLY.
//
// The App Store review build deliberately targets staging: as of 2026-09-08 the Service Grid's
// roster exists only there, so a production build would show Apple's reviewer an empty grid and
// fail guideline 2.1. But the <DevBanner/> ring stamps "DEV Sandbox · not production" over every
// frame, and a reviewer who sees that rejects the build as a test version — the same guideline,
// by a different route.
//
// This flag suppresses ONLY the visual marker. It does not change which project the app talks to,
// does not touch IS_DEV, and cannot be set by an ordinary build: scripts/prepare-ipad-app.mjs sets
// it exclusively on the --allow-staging path, so the decision to hide the badge lives beside the
// decision to ship staging at all, and neither can happen silently.
export const HIDE_ENV_BADGE = import.meta.env.VITE_HIDE_ENV_BADGE === 'true'

const app = initializeApp(firebaseConfig)

// ---- App Check: bot/abuse protection for Firestore, Functions & Storage ----
// reCAPTCHA Enterprise site key for the PRODUCTION project (public by design).
// App Check is per-project, so this runs only for prod; when staging goes live,
// add its own key here (keyed by firebaseConfig.projectId).
if (firebaseConfig.projectId === 'fitproof-inciteos' && import.meta.env.VITE_USE_EMULATORS !== 'true') {
  // In dev, use an App Check debug token so localhost works without real attestation.
  // The first `npm run dev` prints a token to the browser console — register it once at
  // Firebase Console → App Check → Apps → (web app) → Manage debug tokens.
  if (import.meta.env.DEV) {
    self.FIREBASE_APPCHECK_DEBUG_TOKEN = true
  }
  initializeAppCheck(app, {
    provider: new ReCaptchaEnterpriseProvider('6Lf7AyUtAAAAAHe-JVBxyFOdWXhklovTMS_Y0vyJ'),
    isTokenAutoRefreshEnabled: true,
  })
}

export const auth = getAuth(app)
export const db = getFirestore(app)

// When running `npm run emulators`, point the SDK at local emulators.
if (import.meta.env.VITE_USE_EMULATORS === 'true') {
  connectAuthEmulator(auth, 'http://127.0.0.1:9099', { disableWarnings: true })
  connectFirestoreEmulator(db, '127.0.0.1', 8080)
}

// ---------------------------------------------------------------------------------------------
// THE KIOSK'S OWN FIREBASE APP — a SECOND, ISOLATED instance.
//
// WHY THIS EXISTS. Firebase auth is scoped to the (app name, origin) pair, and every tab on an
// origin shares one session. The kiosk signs in ANONYMOUSLY, so on the default app it fought with
// the staff session in both directions:
//
//   opening the kiosk replaced an admin's session with an anonymous one — the admin's other tabs
//   then rendered "Almost there · You signed in as ," with an empty email, because an anonymous
//   user has no org claim;
//   and the kiosk's `if (auth.currentUser) return` happily ADOPTED a staff session, so a tablet
//   could end up running as whoever last signed in on that device.
//
// A named second app gets its own auth state and its own IndexedDB persistence key, so the two
// sessions coexist. The tablet is anonymous, the admin stays signed in, and neither can clobber
// the other.
export const kioskApp = initializeApp(firebaseConfig, 'kiosk')
export const kioskAuth = getAuth(kioskApp)
// LONG POLLING, BECAUSE THIS INSTANCE LIVES IN A WEBVIEW.
//
// Firestore's default transport is WebChannel, a streaming connection. Inside WKWebView it stalls:
// measured across 93 sign-in writes on staging, comparing the client's own clock against the time
// Firestore accepted the document —
//
//   Admin SDK (no WebChannel) : 36 writes, 0 over 5s, max 0.9s
//   Safari                    : 17 writes, 6% over 5s, p90 0.8s
//   home-screen WebView       : 25 writes, 16% over 5s, p90 10.5s
//   native app (WKWebView)    : 15 writes, 40% over 5s, p90 12.8s, max 17.7s
//
// app vs browser is z=2.33 (p≈0.02), and only the two WebView-hosted clients stall. That is a
// transport problem, not our code: the function itself executes in ~1.2s every time, and the delay
// sits BEFORE Firestore accepts the write, not after.
//
// Sean recorded it for App Review on 2026-09-10 — 15+ seconds of "Checking…" on camera, which a
// reviewer may reasonably read as a broken app. autoDetect probes the connection and falls back to
// long polling when streaming does not work, rather than forcing it everywhere.
//
// ONLY the kiosk instance changes. The portal's `db` above keeps the default transport — it runs in
// a real browser, where WebChannel is both fine and faster.
export const kioskDb = initializeFirestore(kioskApp, { experimentalAutoDetectLongPolling: true })
// The kiosk app's Storage (the Service Grid's photo evidence) is kioskStorage in ./firebaseStorage.

if (import.meta.env.VITE_USE_EMULATORS === 'true') {
  connectAuthEmulator(kioskAuth, 'http://127.0.0.1:9099', { disableWarnings: true })
  connectFirestoreEmulator(kioskDb, '127.0.0.1', 8080)
}

export default app
